/**
 * Site-wide search omnibar.
 *
 * Header button opens a full-screen modal with a debounced live search that
 * hits `/api/search`. Results are grouped by content type; hitting Enter
 * (or the "See all results" link) jumps to `/search?q=…` for the full view.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2, ArrowRight, CornerDownLeft } from "lucide-react";
import { http } from "@/lib/api";

const TYPE_ICON_COLORS = {
  news: "#1D4ED8",
  memorial: "#5B4A73",
  event: "#C47F3A",
  cause: "#B23A3A",
  world_changer: "#5C6B52",
  story: "#8A5CF0",
  organization: "#0F766E",
  housing: "#7C3AED",
  recipe: "#EA580C",
  service: "#6B7280",
};

export default function SearchOmnibar({ open, onClose }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Focus input when opened; ESC to close.
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 60);
    }
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Debounced fetch.
  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setData(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await http.get("/search", { params: { q, per_type: 3 } });
        setData(r.data);
      } catch {
        setData({ groups: [], total: 0 });
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  const jumpToFullResults = () => {
    if (q.trim().length < 2) return;
    onClose();
    nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const flat = useMemo(() => {
    const arr = [];
    for (const g of data?.groups || []) {
      for (const it of g.items) arr.push({ ...it, group_label: g.label });
    }
    return arr;
  }, [data]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center pt-16 px-4"
      style={{ background: "rgba(20,33,61,0.30)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="search-omnibar"
    >
      <div
        className="w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden"
        style={{ background: "var(--wofy-paper)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <form
          onSubmit={(e) => { e.preventDefault(); jumpToFullResults(); }}
          className="flex items-center gap-3 px-5 py-4 border-b"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <Search className="w-5 h-5 shrink-0" style={{ color: "var(--wofy-text-soft)" }} />
          <input
            ref={inputRef}
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search news, memorials, events, causes, recipes, people…"
            className="flex-1 bg-transparent outline-none text-base"
            style={{ color: "var(--wofy-text)" }}
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--wofy-text-soft)" }} />}
          <button
            type="button"
            onClick={onClose}
            className="opacity-60 hover:opacity-100"
            aria-label="Close search"
            data-testid="search-close"
          >
            <X className="w-5 h-5" />
          </button>
        </form>

        {/* Results */}
        <div className="max-h-[65vh] overflow-y-auto">
          {q.trim().length < 2 ? (
            <div className="px-6 py-10 text-center text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              Type at least 2 characters to search.
              <div className="mt-3 flex flex-wrap gap-1.5 justify-center">
                {["Nelson Mandela", "Housing", "Recipes", "Weather", "Volunteer"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setQ(s)}
                    className="px-2.5 py-1 rounded-full border text-xs"
                    style={{ borderColor: "var(--wofy-border)" }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : !data ? null : flat.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              No matches for "<b>{q}</b>". Try broader terms.
            </div>
          ) : (
            <>
              {data.groups.map((g) => (
                <div key={g.type} data-testid={`search-group-${g.type}`}>
                  <div
                    className="px-5 py-2 text-[10px] font-mono uppercase tracking-wider sticky top-0"
                    style={{ color: "var(--wofy-text-soft)", background: "var(--wofy-paper)" }}
                  >
                    {g.label} · {g.count}
                  </div>
                  <ul>
                    {g.items.map((it) => (
                      <li key={`${it.type}-${it.id}`}>
                        <button
                          type="button"
                          data-testid={`search-hit-${it.type}-${it.id}`}
                          onClick={() => { onClose(); nav(it.url); }}
                          className="w-full flex items-start gap-3 px-5 py-3 text-left hover:bg-[color:var(--wofy-surface)]/60 border-t"
                          style={{ borderColor: "var(--wofy-line)" }}
                        >
                          <div
                            className="w-10 h-10 rounded-md overflow-hidden shrink-0 flex items-center justify-center"
                            style={{ background: (TYPE_ICON_COLORS[it.type] || "#666") + "22", color: TYPE_ICON_COLORS[it.type] || "#666" }}
                          >
                            {it.image ? (
                              <img src={it.image} alt="" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                            ) : (
                              <Search className="w-4 h-4" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold truncate">{it.title}</div>
                            {(it.subtitle || it.meta) && (
                              <div className="text-[11px] font-mono uppercase tracking-wider truncate" style={{ color: "var(--wofy-text-soft)" }}>
                                {[it.subtitle, it.meta].filter(Boolean).join(" · ")}
                              </div>
                            )}
                            {it.snippet && (
                              <div className="text-xs mt-0.5 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>
                                {it.snippet}
                              </div>
                            )}
                          </div>
                          <ArrowRight className="w-4 h-4 opacity-40 mt-1.5 shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        {q.trim().length >= 2 && data && flat.length > 0 && (
          <div
            className="px-5 py-3 border-t flex items-center justify-between text-xs"
            style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}
          >
            <span>{data.total} match{data.total === 1 ? "" : "es"} across {data.groups.length} section{data.groups.length === 1 ? "" : "s"}</span>
            <button
              type="button"
              onClick={jumpToFullResults}
              data-testid="search-see-all"
              className="inline-flex items-center gap-1 font-semibold"
              style={{ color: "var(--wofy-primary)" }}
            >
              See all results <CornerDownLeft className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
